import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Crown, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import confetti from 'canvas-confetti';

const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    // Launch confetti
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 }
    });

    // Countdown redirect
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate('/account');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full text-center"
      >
        {/* Success Animation */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
          className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-olive-400 to-olive-500 flex items-center justify-center shadow-2xl shadow-olive-500/30"
        >
          <CheckCircle className="w-12 h-12 text-white" />
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="text-3xl font-bold text-stone-900 mb-2"
        >
          Welcome to Premium!
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-stone-600 mb-8"
        >
          Your subscription is now active. Enjoy unlimited fashion discovery!
        </motion.p>

        {/* Features */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-2xl p-6 shadow-xl border border-stone-200 mb-8"
        >
          <div className="flex items-center justify-center gap-2 mb-4">
            <Crown className="w-5 h-5 text-yellow-500" />
            <span className="font-semibold text-stone-600">Premium Features Unlocked</span>
          </div>
          
          <div className="space-y-3 text-left">
            <div className="flex items-center gap-3 text-stone-700">
              <Sparkles className="w-4 h-4 text-stone-500" />
              <span>Unlimited AI chats & styling advice</span>
            </div>
            <div className="flex items-center gap-3 text-stone-700">
              <Sparkles className="w-4 h-4 text-stone-500" />
              <span>40 standard virtual try-ons/month</span>
            </div>
            <div className="flex items-center gap-3 text-stone-700">
              <Sparkles className="w-4 h-4 text-stone-500" />
              <span>Full AI Studio — videos, lookbooks, 360° views</span>
            </div>
            <div className="flex items-center gap-3 text-stone-700">
              <Sparkles className="w-4 h-4 text-stone-500" />
              <span>15 creative visions, 25 image edits, 6 bulk uploads</span>
            </div>
          </div>
        </motion.div>

        {/* Action */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <Button
            onClick={() => navigate('/home')}
            className="w-full py-6 text-lg bg-gradient-to-r from-stone-600 to-terracotta-600 hover:from-stone-700 hover:to-terracotta-700 text-white mb-4"
          >
            Start Exploring
          </Button>
          
          <p className="text-sm text-stone-500">
            Redirecting to your account in {countdown}s...
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default SubscriptionSuccess;
